/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import type { Span } from "@sentry/react"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { InitialScrsTable } from "services/computations/types"
import { Sentry } from "services/sentry"
import { DeepReadonly } from "utils/readonly"

import {
  ABORT_PHASE_TAG,
  AbortPhaseError,
  BodyDeadline,
  BodyDeadlineDetails,
  FetchAbortPhase,
  INITIAL_SCRS_REQUEST,
  abortPhaseOf,
  withAbortPhase,
} from "./requestContext"

/**
 * Valid HTTP status codes that should trigger a retry.
 *
 * Every member is a failure the *server* reports as transient, where the same request
 * repeated stands a real chance of succeeding: a timeout (408) or one of the 5xx family.
 *
 * 429 is deliberately absent (#721). It is the one status that says the fault is the
 * client's own request *rate*, so repeating the request is the single response guaranteed
 * not to help - it spends the caller's rate budget confirming the budget is spent. nginx
 * answers a `limit_req` rejection with exactly this status (`limit_req_status 429` in
 * nginx/prod/default.conf), so retrying it turned one throttled submission into four requests and
 * forced the perimeter limit to be sized four times looser than real traffic needs.
 */
// AIDEV-NOTE (#721): 503 stays retryable and that is not an oversight. It means the *server*
// is unavailable, not that this client is over quota - the backend's own capacity shed
// (backend/swr_toolbox/core/concurrency_limit.py, CAPACITY_EXCEEDED_STATUS) returns it with a
// `Retry-After` precisely so a client comes back. What is still missing is that the header is
// ignored in favour of calculateBackoff; honouring it is tracked separately, not here.
export const RETRY_STATUS_CODES = [408, 500, 502, 503, 504] as const

/**
 * Type for retry status codes
 */
export type RetryStatusCode = (typeof RETRY_STATUS_CODES)[number]

/**
 * Type for the configuration with more specific types
 */
export interface FetchConfig {
  readonly maxRetries: number
  readonly retryDelay: number
  /** Milliseconds each attempt waits for response headers. Governs the header phase only. */
  readonly timeout: number
  readonly retryStatusCodes: readonly number[]
  /**
   * Milliseconds a successful response's body may go without a single byte arriving before the
   * read is abandoned as stalled.
   */
  readonly bodyIdleTimeout: number
  /**
   * Milliseconds from headers arriving that a successful response's body may take in total,
   * however steadily its bytes are arriving.
   */
  readonly bodyTotalTimeout: number
}

/**
 * Default configuration for fetch operations
 */
// AIDEV-NOTE (#1554): the two body deadlines are a decision, not values inherited from
// `timeout`. The response is ~218 KB uncompressed on the wire (measured on #484). Under the old
// single 10s body deadline that needed ~22 KB/s sustained, and a slower but working link failed
// every time, without retry (#478). The 30s total cap lowers that to ~7.3 KB/s (218 / 30), and
// to ~3.2 KB/s (96 / 30) once #484 gzips the response: gzip measured 217,548 bytes down to
// 96,390, 44% of the original or ~2.3x smaller (#484's 2026-10-26 measurement, for PR #1581).
// The idle deadline is what detects a genuine stall: 10s with no byte at all. The total cap is
// not a stall detector; it exists only so a body that trickles forever cannot recreate the #51
// endless spinner.
//
// `timeout` was deliberately not raised instead. It also sets the header-phase deadline, so
// raising it would lengthen every header wait and the 54-61s retry budget that #990 and #992
// are about, to fix a problem that only ever occurs after headers have arrived.
export const DEFAULT_FETCH_CONFIG: FetchConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 10000,
  retryStatusCodes: RETRY_STATUS_CODES,
  bodyIdleTimeout: 10_000,
  bodyTotalTimeout: 30_000,
}

const REQUIRED_INITIAL_SCRS_FIELDS = [
  "initial_scr",
  "year",
  "month",
  "caey_ern_1M_lag",
  "caey_shiller_1M_lag",
  "real_equity_drawdown_1M_lag",
  "c1",
  "sum_of_ct",
  "sum_of_ctpt",
] as const satisfies readonly (keyof InitialScrsTable)[]

// Compile-time exhaustiveness check: fails to typecheck if a key is added to (or
// renamed on) InitialScrsTable without updating REQUIRED_INITIAL_SCRS_FIELDS, so the
// runtime validation below can't silently drift from the interface it's meant to guard.
// A bare `satisfies` statement rather than a typed const, so there is no binding that
// noUnusedLocals would report as never read.
type AssertAllInitialScrsFieldsListed =
  keyof InitialScrsTable extends (typeof REQUIRED_INITIAL_SCRS_FIELDS)[number] ? true : never
true satisfies AssertAllInitialScrsFieldsListed

/**
 * Structured reason a compute-initial-scrs response failed validation, so callers can report
 * *why* (e.g. which field, which index, NaN vs. wrong type) rather than a generic failure.
 */
type InitialScrsValidationError =
  | { readonly reason: "not-object" }
  | { readonly reason: "missing-field"; readonly field: string }
  | { readonly reason: "not-array"; readonly field: string }
  | { readonly reason: "non-numeric-value"; readonly field: string; readonly index: number }
  | { readonly reason: "nan-value"; readonly field: string; readonly index: number }

/**
 * Validates that the response matches the InitialScrsTable interface, returning the specific
 * reason for failure (or null if valid) instead of a plain boolean.
 */
function validateInitialScrsResponse(data: unknown): InitialScrsValidationError | null {
  if (!data || typeof data !== "object") {
    return { reason: "not-object" }
  }

  const record = data as Record<string, unknown>

  // Check that all required fields exist and are arrays
  for (const field of REQUIRED_INITIAL_SCRS_FIELDS) {
    if (!(field in record)) {
      return { reason: "missing-field", field }
    }
    if (!Array.isArray(record[field])) {
      return { reason: "not-array", field }
    }
  }

  // Check that all arrays contain only finite numbers, distinguishing NaN from other
  // non-numeric values since `typeof NaN === "number"` would otherwise pass silently.
  for (const field of REQUIRED_INITIAL_SCRS_FIELDS) {
    const array = record[field] as unknown[]
    for (let index = 0; index < array.length; index++) {
      const value = array[index]
      if (typeof value !== "number") {
        return { reason: "non-numeric-value", field, index }
      }
      if (Number.isNaN(value)) {
        // Defensive: backend/swr_toolbox/models/initial_scrs_response.py replaces NaN with
        // null before serializing, and JSON has no NaN literal, so response.json() can't
        // actually produce this in practice. Kept in case that backend guarantee ever changes.
        return { reason: "nan-value", field, index }
      }
    }
  }

  return null
}

/**
 * Renders a validation error as human-readable detail suitable for an Error message or toast.
 */
function describeInitialScrsValidationError(error: InitialScrsValidationError): string {
  switch (error.reason) {
    case "not-object":
      return "response was not a JSON object"
    case "missing-field":
      return `missing required field "${error.field}"`
    case "not-array":
      return `field "${error.field}" is not an array`
    case "non-numeric-value":
      return `field "${error.field}" contains a non-numeric value at index ${error.index}`
    case "nan-value":
      return `field "${error.field}" contains NaN at index ${error.index}`
  }
}

/**
 * Creates an AbortController that will timeout after the specified milliseconds
 */
function createTimeoutController(timeoutMs: number): {
  controller: AbortController
  timeoutId: NodeJS.Timeout
} {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, timeoutMs)
  return { controller, timeoutId }
}

/**
 * Sleep function that returns a promise resolving after the specified delay. Accepts an
 * optional AbortSignal so a caller that no longer wants a result can cut short a pending
 * backoff wait instead of it running to completion regardless (#479).
 */
// AIDEV-NOTE: AbortSignal is a DOM lib type we don't own and can't mark readonly; wrapping
// it in Readonly<AbortSignal> wouldn't change anything meaningful since we only ever read
// signal.aborted/signal.reason and call its own (unrelated) addEventListener method.
// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(toRejectionError(signal.reason))
      return
    }
    // AIDEV-NOTE: `{ once: true }` only detaches the listener if `abort` actually fires, so
    // the timer-wins path has to detach it explicitly. The signal outlives any single wait -
    // one controller covers a whole submission and every retry adds another backoff - so a
    // listener left behind per wait accumulates on that signal until the submission ends.
    // A function declaration (hoisted) rather than a const arrow, so the two can reference
    // each other: the listener clears the timer, and the timer detaches the listener.
    function onAbort(): void {
      clearTimeout(timeoutId)
      reject(toRejectionError(signal?.reason))
    }
    // AIDEV-NOTE: attach before arming, not after. If setTimeout runs its callback
    // synchronously - which a test double can, and which is invisible from here - arming
    // first would run the detach before the listener existed, making it a no-op and leaving
    // the listener attached forever. Attaching first makes the pairing hold under any timer
    // implementation rather than relying on real timers being asynchronous.
    signal?.addEventListener("abort", onAbort, { once: true })
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
  })
}

/**
 * `AbortSignal.reason` is typed `any` and, if a caller ever passes a custom reason to
 * `abort(reason)`, isn't guaranteed to be an Error - reject with it unchanged in that case
 * would break `error instanceof Error`/`DOMException` checks downstream. The default reason
 * set by a bare `abort()` call already is a DOMException (itself an Error), so this passes it
 * through unchanged in the common case.
 */
function toRejectionError(reason: unknown): Error {
  return reason instanceof Error ? reason : new Error(String(reason))
}

/**
 * Upper bound on how much of a non-ok body is read into memory. Sized so a real 422 survives
 * intact rather than for a typical error string: the ValidationError handler in
 * backend/swr_toolbox/main.py keeps Pydantic's `input` key, which for missing-field and
 * model-level errors holds the *entire* submitted payload, repeated once per error. A
 * parameters payload carrying a populated `cashFlows` array runs to several KB, so a handful
 * of errors clears a small cap easily - and truncating a JSON body doesn't cost a few
 * characters of detail, it costs all of it, because the remainder no longer parses. 64 KiB
 * accommodates roughly eight echoes of a large payload while still bounding what an unbounded
 * upstream body (HTML error page, debug traceback) can cost.
 */
const MAX_ERROR_BODY_CHARS = 65536

/**
 * Deadline for reading a non-ok response body, clamped against the caller's timeout at the
 * point of use. Sized for delivering a small error payload, not for the backend computation
 * that `FetchConfig.timeout` covers.
 */
export const ERROR_BODY_READ_TIMEOUT_MS = 2000

/**
 * Upper bound on consecutive reads that decode to nothing before the read loop below gives up.
 * A `read()` resolving `{ done: false }` is not obliged to advance the decoded length: chunked
 * transfer may emit empty data frames, and a chunk carrying only part of a multi-byte character
 * decodes to `""` under `{ stream: true }` until the bytes completing it arrive. Neither is an
 * error, so decoded length alone cannot terminate the loop - a peer serving only such chunks
 * never reaches the byte cap and spins the microtask queue until an outside deadline aborts the
 * stream. Counting *consecutive* non-progressing reads, rather than capping reads outright,
 * bounds the loop without truncating a slow-but-progressing body: a partial UTF-8 sequence needs
 * at most three further reads to complete, and the remainder is headroom for empty frames
 * interleaved with real ones.
 */
const MAX_NON_PROGRESSING_READS = 8

/**
 * Reads a non-ok response body so its underlying connection is released rather than left open
 * until GC reclaims it (#477), returning the text for the caller to fold into an error message.
 * A body that fails part-way through yields what was read up to that point; null is reserved for
 * a body that could not be read at all.
 */
// AIDEV-NOTE: this reads incrementally and cancels at MAX_ERROR_BODY_CHARS rather than calling
// response.text(). text() buffers the whole body before the caller uses at most a few hundred
// characters of it, so an upstream returning a large error page or a debug traceback is
// materialized in full, on every attempt. Cancelling the reader releases the connection just
// as a completed text() would, so #477's goal is unaffected. response.body is absent on some
// Response implementations (and on the test doubles in this suite), so text() remains the
// fallback.
// AIDEV-NOTE: Response is a DOM lib type we don't own; see the sleep() note above.
// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
async function consumeResponseBody(response: Response): Promise<string | null> {
  try {
    if (!response.body) {
      const text = await response.text()
      return text.slice(0, MAX_ERROR_BODY_CHARS)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let text = ""
    let nonProgressingReads = 0
    try {
      while (
        text.length < MAX_ERROR_BODY_CHARS &&
        nonProgressingReads < MAX_NON_PROGRESSING_READS
      ) {
        const { done, value } = await reader.read()
        if (done) break
        const charsBeforeChunk = text.length
        text += decoder.decode(value, { stream: true })
        nonProgressingReads = text.length > charsBeforeChunk ? 0 : nonProgressingReads + 1
      }
    } catch {
      // A read rejecting part-way through a body - the caller's error-body deadline firing once
      // most of a chunked payload has arrived, a proxy dropping the connection - leaves what was
      // read intact and worth returning. Partial JSON won't parse, so describeNonOkResponse
      // degrades to the status line for the shape this endpoint usually returns, but a partial
      // plain-text or HTML body still carries more than nothing. The read error itself has no
      // audience: this function's contract is text-or-null, and its caller is composing a
      // message about a status it already knows.
    } finally {
      // Releasing the connection is best-effort: neither a cancel failure nor a read failure
      // may discard text already read. cancel() can fail two ways: a rejected promise (the
      // .catch) or a synchronous throw (this try/catch). An unguarded synchronous throw escapes
      // to the outer catch, which returns null - losing the entire body over a
      // connection-release failure that changes nothing for the caller.
      try {
        void reader.cancel().catch(() => undefined)
      } catch {
        // Deliberately swallowed - see above.
      }
    }
    // A chunk boundary can fall inside a multi-byte character, and `stream: true` holds those
    // bytes back until the ones completing it arrive. A body that ends - or is cut off at the
    // cap, or abandoned by a failed read - mid-character never sends them, so without a final
    // flush they are dropped with no trace in the decoded text.
    text += decoder.decode()
    return text.slice(0, MAX_ERROR_BODY_CHARS)
  } catch {
    return null
  }
}

/**
 * Upper bound on server-supplied text folded into a user-facing error message. Long enough
 * for any real FastAPI validation detail, short enough that a runaway body can't fill a toast.
 */
const MAX_ERROR_DETAIL_LENGTH = 300

/**
 * Upper bound on how many Pydantic error entries are folded into one message. A 422 reports
 * every invalid field, and this text lands in a toast the user reads at a glance: the first
 * few entries are actionable, a dozen near-identical lines are not.
 */
const MAX_VALIDATION_ENTRIES_DESCRIBED = 3

/**
 * Narrows a parsed-JSON value to an array without letting `any` leak out of `Array.isArray`.
 */
function asUnknownArray(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) ? (value as readonly unknown[]) : null
}

/**
 * Names the offending field from a Pydantic error's `loc` path. FastAPI prefixes body paths
 * with the request part, which is noise to the user, and array elements appear as numeric
 * segments - so `["body", "cashFlows", 0, "amount"]` reads as `cashFlows.0.amount`.
 */
function describeValidationLocation(location: unknown): string {
  const segments = asUnknownArray(location)
  if (!segments) {
    return ""
  }
  const named = segments.filter(
    (segment): segment is string | number =>
      typeof segment === "string" || typeof segment === "number"
  )
  const fields = named[0] === "body" ? named.slice(1) : named
  return fields.join(".")
}

/**
 * Renders one Pydantic error entry as `field: message`, or null when it carries neither.
 */
function describeValidationEntry(entry: unknown): string | null {
  if (typeof entry === "string") {
    return entry.length > 0 ? entry : null
  }
  if (!entry || typeof entry !== "object") {
    return null
  }
  const record = entry as Record<string, unknown>
  const message = typeof record.msg === "string" && record.msg.length > 0 ? record.msg : null
  const field = describeValidationLocation(record.loc)
  if (message === null) {
    return field.length > 0 ? field : null
  }
  return field.length > 0 ? `${field}: ${message}` : message
}

/**
 * Renders a FastAPI `detail` payload as user-facing text, or null when it holds nothing
 * readable. Two shapes are real here and only the first was previously handled: a plain string
 * (`HTTPException(detail=...)`, e.g. the data-access 500 in api/v2/router.py) and a *list* of
 * Pydantic error objects, which is what main.py's ValidationError handler returns and is
 * therefore the only 4xx this endpoint produces. Entries are treated as untrusted - `loc` or
 * `msg` may be absent or the wrong type - because degrading to the status line is always
 * better than throwing while composing an error message.
 */
function describeResponseDetail(detail: unknown): string | null {
  if (typeof detail === "string") {
    return detail.length > 0 ? detail : null
  }
  const entries = asUnknownArray(detail)
  if (!entries) {
    return null
  }
  const described = entries
    .map(describeValidationEntry)
    .filter((entry): entry is string => entry !== null)
  if (described.length === 0) {
    return null
  }
  const shown = described.slice(0, MAX_VALIDATION_ENTRIES_DESCRIBED)
  const hidden = described.length - shown.length
  return hidden > 0 ? `${shown.join("; ")} (+${hidden} more)` : shown.join("; ")
}

/**
 * Describes a non-ok response for an error message: uses the FastAPI `detail` field from the
 * body when it holds something readable (see describeResponseDetail for the two shapes it
 * takes), and otherwise falls back to statusText/status.
 */
// AIDEV-NOTE: this deliberately does NOT fall back to the raw body text. The result reaches
// the user directly (UserParametersForm renders error.message in a toast), and a 502/504 from
// nginx has an HTML error page as its body - surfacing that verbatim replaced a readable "Bad
// Gateway" with markup plus the nginx version string, and is unbounded in length if an
// upstream ever returns a large body or a debug traceback. Only the structured, intentional
// `detail` field is trusted for display; anything else degrades to the status line.
// AIDEV-NOTE: Response is a DOM lib type we don't own; see the sleep() note above.
// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
function describeNonOkResponse(bodyText: string | null, response: Response): string {
  const statusDescription = response.statusText || String(response.status)
  if (!bodyText) {
    return statusDescription
  }
  try {
    const parsed = JSON.parse(bodyText) as unknown
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const described = describeResponseDetail((parsed as Record<string, unknown>).detail)
      if (described !== null) {
        return described.length > MAX_ERROR_DETAIL_LENGTH
          ? `${described.slice(0, MAX_ERROR_DETAIL_LENGTH)}...`
          : described
      }
    }
  } catch {
    // Body wasn't JSON - fall through to the status line.
  }
  return statusDescription
}

/**
 * Message when the idle deadline fired: no byte of the body arrived for `bodyIdleTimeout`.
 */
// AIDEV-NOTE (#1554): this is the message every body-phase event carried before the deadline
// was split, and Sentry groups on exception type plus value. Keeping it byte-for-byte for the
// idle case keeps genuine stalls in JAVASCRIPT-REACT-30 with their history; the total-cap case
// says something different, so it is right that it opens a group of its own.
const BODY_IDLE_TIMEOUT_MESSAGE =
  "Response body stalled while fetching initial SCRs; the backend computation had already completed"

/**
 * Message when the total cap fired: bytes were still arriving at `bodyTotalTimeout`.
 */
const BODY_TOTAL_TIMEOUT_MESSAGE =
  "Response body was still arriving when its total deadline expired while fetching initial SCRs; the backend computation had already completed"

/**
 * Thrown when a successful response's body misses one of its two deadlines after headers have
 * already arrived: no byte for `bodyIdleTimeout`, or not complete within `bodyTotalTimeout`.
 * Distinguished from a header-phase timeout because the backend had, by definition, already
 * finished the computation by the time headers arrived - retrying would re-run completed work
 * rather than address the delivery fault (#478).
 */
export class InitialScrsBodyPhaseTimeoutError
  extends Error
  implements AbortPhaseError, BodyDeadlineDetails
{
  // Read by `abortPhaseOf`, which is what turns the phase into a Sentry tag (#617). Declared
  // here rather than assigned by `withAbortPhase` because this class is free to say what it is:
  // it already has its own name and message, so nothing about its Sentry grouping is at stake.
  readonly abortPhase: FetchAbortPhase = "body"
  // Read by `bodyDeadlineDetailsOf`, which turns them into a Sentry tag and extras (#1554).
  readonly deadline: BodyDeadline
  readonly bytesReceived: number | null
  readonly msSinceHeaders: number
  // AIDEV-NOTE (#1554): not directly comparable with `bytesReceived`. The reader counts decoded
  // bytes - after the browser has undone any Content-Encoding - while Content-Length counts the
  // bytes on the wire. They agree today only because the response is sent uncompressed; once
  // #484 enables compression they will not, and nginx's gzip typically drops Content-Length in
  // favour of chunked transfer, so this will mostly be null.
  readonly expectedBytes: number | null

  constructor(details: BodyDeadlineDetails) {
    super(details.deadline === "idle" ? BODY_IDLE_TIMEOUT_MESSAGE : BODY_TOTAL_TIMEOUT_MESSAGE)
    this.name = "InitialScrsBodyPhaseTimeoutError"
    this.deadline = details.deadline
    this.bytesReceived = details.bytesReceived
    this.msSinceHeaders = details.msSinceHeaders
    this.expectedBytes = details.expectedBytes
  }
}

/**
 * Parses a `Content-Length` header value into a byte count, or null when it is absent or is
 * not a single non-negative integer. A repeated header arrives from `Headers.get` comma-joined
 * ("12, 12"), which this rejects rather than guessing which copy to believe.
 */
const parseContentLength = (value: string | null): number | null => {
  if (value === null || !/^\d+$/.test(value)) {
    return null
  }
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) ? parsed : null
}

/**
 * Message for a header-phase timeout: no response headers within `config.timeout`, on every
 * attempt. Held as a constant because it is load-bearing beyond this file - it is the value
 * half of the Sentry grouping key for `JAVASCRIPT-REACT-2A`, the production issue #843 tracks.
 */
const HEADER_PHASE_TIMEOUT_MESSAGE = "Request timed out while fetching initial SCRs"

/**
 * Builds the header-phase timeout error, tagged with its phase for reporting.
 */
// AIDEV-NOTE (#617): deliberately a plain `Error` and not a named subclass, unlike its
// body-phase counterpart above. Sentry groups JavaScript errors on exception type and value,
// where the type is `error.name` - so giving this its own name would open a *new* Sentry issue
// and detach every future occurrence from JAVASCRIPT-REACT-2A, which is the three-occurrence
// history #843 is waiting on a recurrence of. The phase therefore travels as a property, which
// `beforeSend` in services/sentry.ts turns into a searchable tag without touching grouping.
const createHeaderPhaseTimeoutError = (): AbortPhaseError =>
  withAbortPhase(new Error(HEADER_PHASE_TIMEOUT_MESSAGE), "headers")

/**
 * Thrown when the caller's `externalSignal` is aborted - either already aborted on entry, or
 * aborted while the call was mid-flight. A cancellation the caller asked for is not a fault to
 * report, but without a distinct type it arrives as a bare abort DOMException whose message is
 * whatever the browser chose ("signal is aborted without reason" in Chrome), which no caller
 * can tell apart from a real failure before rendering it in a toast or shipping it to Sentry.
 * The abort that caused it is preserved as `cause`.
 */
export class InitialScrsCancelledError extends Error {
  // Declared explicitly because the ES2020 lib this project targets predates
  // `Error(message, { cause })`, so the base class has no such property to populate. Typed
  // `unknown` and stored verbatim rather than coerced through toRejectionError: `abort(reason)`
  // accepts any value, and the whole point of a cause is that the original arrives intact.
  readonly cause?: unknown

  constructor(cause?: unknown) {
    super("Fetching initial SCRs was cancelled by the caller")
    this.name = "InitialScrsCancelledError"
    this.cause = cause
  }
}

/**
 * Calculate exponential backoff delay with jitter
 */
const calculateBackoff = (attempt: number, baseDelay: number): number => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt)
  const jitter = Math.random() * (exponentialDelay / 2)
  return exponentialDelay + jitter
}

/**
 * Name of the client-side span covering one `fetchInitialScrs` call, including its retries.
 *
 * Deliberately different from the `POST /api/v2/compute-initial-scrs` name given to each
 * individual attempt below, so a Sentry query can ask for the user-observed total and the
 * per-attempt latency separately rather than getting an indistinguishable mixture of the two.
 */
const COMPUTE_INITIAL_SCRS_SPAN_NAME = "compute-initial-scrs"

/**
 * Op for the outer span, deliberately *not* `http.client`.
 *
 * The outer span covers up to four HTTP requests plus the backoff waits between them, so it is
 * not one request. Sentry's Requests/HTTP insights aggregate by `span.op`, and giving the
 * wrapper the same op as its own children would count every submission twice and mix a
 * non-URL description into a view keyed on URLs. A distinct op keeps the wrapper queryable
 * without polluting the per-request aggregate.
 */
const COMPUTE_INITIAL_SCRS_SPAN_OP = "http.client.with_retries"

/**
 * Name and op of the per-attempt child spans, matching what the SDK's own fetch
 * instrumentation would have produced (`${method} ${url}`, op `http.client`) so the HTTP
 * insights view sees exactly one span per real request, in its usual shape.
 */
const ATTEMPT_SPAN_NAME = `${INITIAL_SCRS_REQUEST.method} ${INITIAL_SCRS_REQUEST.endpoint}`
const ATTEMPT_SPAN_OP = "http.client"

/**
 * Span attribute holding how many retries have been spent so far. OpenTelemetry's name for it,
 * kept updated per attempt rather than written once at the end so a call that ends in a timeout
 * still records whether the deadline fired once or four times - the question #843 asks.
 */
const RESEND_COUNT_ATTRIBUTE = "http.request.resend_count"

/**
 * Waits out one backoff interval, reporting a wait the caller cut short as the typed
 * cancellation rather than as whatever the abort rejected with.
 *
 * Two of the retry loop's three backoff waits sit inside its `catch`, and a rejection raised
 * from a `catch` is not caught by that block's own `try` - so the abort short-circuit at the top
 * of the catch runs too early to convert them. Doing the conversion here instead covers all
 * three uniformly, rather than only the retry-on-status wait that happens to sit in the `try`.
 * `signal.reason` is the cause, matching every other site that constructs this error, so a
 * cancellation carries the abort the caller actually issued regardless of which wait it
 * interrupted.
 */
async function awaitBackoff(
  attempt: number,
  baseDelay: number,
  // AIDEV-NOTE: AbortSignal is a DOM lib type we don't own; see the sleep() note above.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  signal?: AbortSignal
): Promise<void> {
  try {
    await sleep(calculateBackoff(attempt, baseDelay), signal)
  } catch (error) {
    if (signal?.aborted) {
      throw new InitialScrsCancelledError(signal.reason)
    }
    throw error
  }
}

/**
 * What the ok-path body read had seen at the moment one of its deadlines fired.
 */
interface BodyDeadlineExpiry {
  readonly deadline: BodyDeadline
  readonly bytesReceived: number | null
  readonly msSinceHeaders: number
  // AIDEV-NOTE (#1554): a reader rather than a value, called only once the expiry becomes an
  // error - not when headers arrive, and not inside the timer. A deadline firing is rare, so the
  // success path does no header work it would never report; and the response doubles across
  // this suite's tests that model a Response as `{ ok, json }` carry no headers at all, which a
  // read on the success path - or inside a timer that the suite-wide mock fires synchronously -
  // would turn into a TypeError in every one of them.
  readonly readExpectedBytes: () => number | null
}

/**
 * Builds the body-phase error from what its deadline recorded.
 */
const createBodyPhaseTimeoutError = (
  expiry: BodyDeadlineExpiry
): InitialScrsBodyPhaseTimeoutError =>
  new InitialScrsBodyPhaseTimeoutError({
    deadline: expiry.deadline,
    bytesReceived: expiry.bytesReceived,
    msSinceHeaders: expiry.msSinceHeaders,
    expectedBytes: expiry.readExpectedBytes(),
  })

/**
 * Reads and parses a successful response's body under its two deadlines, calling `onDeadline`
 * with what had been read if either fires. Resolves to the parsed JSON, unvalidated.
 *
 * `onDeadline` is expected to abort the request, which errors the body stream and so rejects
 * the pending read; this function does not abort anything itself.
 */
// AIDEV-NOTE (#1554): headers arriving is when both deadlines are armed. The total cap runs from
// there regardless of progress. The idle deadline is re-armed on every chunk that carries at
// least one byte, so it measures the longest *gap*, which is what tells stopped bytes apart from
// slow ones - the old single deadline measured total read time and could not. A zero-length
// chunk is deliberately not progress: chunked transfer may emit empty frames, and a peer sending
// nothing else must still hit the idle deadline. That is the job consumeResponseBody's
// MAX_NON_PROGRESSING_READS does for the error path; here the idle timer does it.
//
// Per-chunk cost is O(1): one clearTimeout/setTimeout pair, one push and one addition. Decoding
// and concatenation happen once, after the last chunk, so the whole read is O(n) in body bytes
// rather than O(n^2) from re-joining a growing buffer per chunk. Decoding once also means a
// multi-byte character split across chunks needs no streaming decoder.
//
// AIDEV-NOTE (#1554): response.body is null on some Response implementations (and on most of the
// test doubles in this suite), as consumeResponseBody's note records. That fallback keeps
// response.json(), which cannot report progress, so only the total cap applies to it and
// `bytesReceived` is null rather than a count that was never taken.
//
// AIDEV-NOTE: this relies on aborting the request erroring its body stream, which is the Fetch
// standard's behaviour and the same contract the response.json() read this replaced relied on.
// Cancelling the reader from the timer instead would resolve the pending read as `done`, and a
// truncated body would then surface as a JSON SyntaxError rather than as a deadline.
// AIDEV-NOTE: Response is a DOM lib type we don't own; see the sleep() note above.
async function readOkResponseBody(
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  response: Response,
  config: FetchConfig,
  onDeadline: (expiry: BodyDeadlineExpiry) => void
): Promise<unknown> {
  const headersArrivedAt = performance.now()
  const body = response.body
  let bytesReceived = 0
  // Arrow factories rather than one shared callback so each timer reports its own deadline;
  // `bytesReceived` is read when the timer fires, not when it is armed.
  const expire = (deadline: BodyDeadline) => (): void => {
    onDeadline({
      deadline,
      bytesReceived: body ? bytesReceived : null,
      msSinceHeaders: Math.round(performance.now() - headersArrivedAt),
      readExpectedBytes: () => parseContentLength(response.headers.get("Content-Length")),
    })
  }

  const totalTimeoutId = setTimeout(expire("total"), config.bodyTotalTimeout)
  let idleTimeoutId: NodeJS.Timeout | null = null
  try {
    if (!body) {
      const data: unknown = await response.json()
      return data
    }

    idleTimeoutId = setTimeout(expire("idle"), config.bodyIdleTimeout)
    const reader = body.getReader()
    const chunks: Uint8Array[] = []
    let finished = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) {
          finished = true
          break
        }
        if (value.byteLength > 0) {
          chunks.push(value)
          bytesReceived += value.byteLength
          clearTimeout(idleTimeoutId)
          idleTimeoutId = setTimeout(expire("idle"), config.bodyIdleTimeout)
        }
      }
    } finally {
      if (!finished) {
        // Best-effort release on every exit short of `done`, as consumeResponseBody does: a
        // cancel that rejects (the .catch) or throws synchronously (the try) must not replace
        // the read failure that is actually propagating - the deadline's AbortError above all,
        // since that is what the caller turns into the body-phase error.
        try {
          void reader.cancel().catch(() => undefined)
        } catch {
          // Deliberately swallowed - see above.
        }
      }
    }

    // Both timers are cleared by the `finally` below. Nothing from here on awaits, so neither
    // can fire while the body is assembled and parsed.
    const bytes = new Uint8Array(bytesReceived)
    let offset = 0
    for (const chunk of chunks) {
      bytes.set(chunk, offset)
      offset += chunk.byteLength
    }
    // A malformed body throws SyntaxError here, as response.json() rejected with before, and
    // the caller propagates it unretried just as it did then.
    const data: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return data
  } finally {
    clearTimeout(totalTimeoutId)
    if (idleTimeoutId !== null) {
      clearTimeout(idleTimeoutId)
    }
  }
}

/**
 * Fetches initial SCRs data from the API with retry logic.
 *
 * `externalSignal` is optional and is NOT full cancellation. It is not threaded into the
 * in-flight request, which is a separate, deliberate decision tracked by #467. Aborting it:
 *
 * - cuts short a pending backoff wait between attempts (#479), and
 * - stops any further retry, reporting an abort-caused failure as an `InitialScrsCancelledError`
 *   and any other failure raised after the abort as itself.
 *
 * An already-aborted signal rejects with the same error before any request is issued.
 *
 * It does not affect the success path: if the caller aborts while a request is in flight and
 * that request then succeeds, this resolves normally with data for a submission the caller no
 * longer wants. Callers needing "ignore the result of a cancelled submission" must still track
 * generations themselves - see the #467 race tests for the pattern.
 *
 * An AbortSignal never resets, so a caller must create a fresh AbortController per submission.
 * Reusing one after a cancellation permanently disables this function for that caller.
 */
export const fetchInitialScrs = async (
  parameters: DeepReadonly<CurrentUserParameters>,
  config: FetchConfig = DEFAULT_FETCH_CONFIG,
  // AIDEV-NOTE: AbortSignal is a DOM lib type we don't own; see the sleep() note above.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  externalSignal?: AbortSignal
): Promise<InitialScrsTable> => {
  // AIDEV-NOTE (#479): an AbortSignal is one-shot - once aborted it stays aborted - so a
  // caller arriving here with one already spent has declared this result unwanted before it
  // was ever asked for. Without this check the request is still dispatched (nothing below
  // consults the external signal before fetch()), and can still resolve successfully, while
  // the first retryable failure rethrows instead of retrying because of the abort
  // short-circuit in the catch block. Fail fast rather than serve a request nobody awaits.
  //
  // Deliberately outside the span below: a call rejected before any request leaves is not a
  // request, and giving it a duration would put a near-zero sample into the very distribution
  // #843 needs to read.
  if (externalSignal?.aborted) {
    throw new InitialScrsCancelledError(externalSignal.reason)
  }

  // AIDEV-NOTE (#877): this span is the only measurement of the request as the *user*
  // experiences it, so it covers the whole retry loop - not the fetch call - and is ended in a
  // `finally` after the body has been read. That interval is what the backend's own
  // http.server span excludes, and what a 10s deadline is actually made of.
  //
  // AIDEV-NOTE (#877): `startInactiveSpan` with `parentSpan: null`, NOT `startSpan`, and both
  // halves of that are load-bearing:
  //
  // - `startSpan` forks the current scope for the lifetime of its callback via
  //   `AsyncContextStack.withScope`, which pushes onto one global stack and clones whatever is
  //   on top (@sentry/core asyncContext/stackStrategy.js `_pushScope`). Two overlapping
  //   submissions - routine here, since `triggerCalculation` is ungated (#467/#474) - therefore
  //   make the second call's span a CHILD of the first's, because `getParentSpan` reads the
  //   span off that cloned scope (trace.js). When the first submission settles first, its
  //   transaction filters children through `isFullFinishedSpan` (sentrySpan.js), which requires
  //   a `timestamp`, so the second, still-open span is dropped; and when the second finally
  //   ends, `_onSpanEnded` returns early because it is not a root span, so it emits nothing.
  //   The concurrent submission's duration - the one most likely to be slow - would be lost
  //   entirely. `withScope` also pops the TOP of that global stack rather than its own entry,
  //   so the fork leaks across submissions and leaves an unrelated scope current.
  // - `parentSpan: null` makes this a root span unconditionally, rather than inheriting
  //   whatever the browser tracing integration happens to have open. An ambient `pageload` or
  //   `ui.interaction.click` root span finishes on an idle timeout in seconds, while this call
  //   can run ~97s at the default config (derived above STUCK_SPINNER_REPORT_DELAY_MS in
  //   UserParametersForm.tsx), and a parent that ends first drops us by the same
  //   `isFullFinishedSpan` filter. The trace is unaffected: with no parent, `_startRootSpan`
  //   takes its traceId from the scope's propagation context (trace.js), so this stays in the
  //   same trace as the pageload rather than starting a new one.
  //
  // Both `startInactiveSpan` wrappers used here (`parentSpan: null` above, `parentSpan: span`
  // in the attempt loop) return synchronously, so their internal push/pop of the scope stack
  // completes before this function awaits anything and no fork outlives the call.
  const span = Sentry.startInactiveSpan({
    name: COMPUTE_INITIAL_SCRS_SPAN_NAME,
    op: COMPUTE_INITIAL_SCRS_SPAN_OP,
    parentSpan: null,
    attributes: {
      "http.request.method": INITIAL_SCRS_REQUEST.method,
      "url.path": INITIAL_SCRS_REQUEST.endpoint,
    },
  })

  try {
    return await runInitialScrsAttempts(parameters, config, span, externalSignal)
  } catch (error) {
    // Deriving the span attribute from the same error the Sentry tag is derived from is
    // what stops the two from ever disagreeing about which phase stalled.
    const phase = abortPhaseOf(error)
    if (phase !== null) {
      span.setAttribute(ABORT_PHASE_TAG, phase)
    }
    // AIDEV-NOTE: no `setStatus({ code: SPAN_STATUS_ERROR })` to match what `startSpan` did
    // automatically on a throw: `SPAN_STATUS_ERROR` is declared in @sentry/core's
    // tracing/spanstatus.d.ts but re-exported from neither its own index nor @sentry/browser's,
    // so setting it means either a deep import into package internals or a hard-coded `2`.
    // The status is cosmetic here - a failed request is found by its duration and by the error
    // event carrying ABORT_PHASE_TAG, not by transaction status - so neither is worth it.
    throw error
  } finally {
    // An aborted request must still leave a duration behind: "a request that times out
    // client-side leaves no timing record on either side" is #843's complaint, and an unended
    // span is no record.
    span.end()
  }
}

/**
 * Runs the request-and-retry loop for `fetchInitialScrs`, inside the span it created.
 *
 * Split out purely so the span wrapper stays legible; the retry semantics documented on
 * `fetchInitialScrs` are all implemented here.
 */
async function runInitialScrsAttempts(
  parameters: DeepReadonly<CurrentUserParameters>,
  config: FetchConfig,
  // AIDEV-NOTE: Span is a Sentry type we don't own; see the sleep() note above. Only
  // setAttribute is called on it, which mutates the span by design.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  span: Span,
  // AIDEV-NOTE: AbortSignal is a DOM lib type we don't own; see the sleep() note above.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  externalSignal?: AbortSignal
): Promise<InitialScrsTable> {
  let attempt = 0

  while (attempt <= config.maxRetries) {
    // Every path that schedules another attempt increments `attempt` and continues, so writing
    // the count here covers all of them in one place and leaves it correct on exit by any route.
    span.setAttribute(RESEND_COUNT_ATTRIBUTE, attempt)

    const { controller, timeoutId } = createTimeoutController(config.timeout)
    let bodyTimeoutId: NodeJS.Timeout | null = null
    // Set by whichever ok-path body deadline fires first, recording which one it was and what
    // the read had seen; null while neither has. One nullable record rather than a flag plus
    // separate fields, so "timed out" without saying which deadline cannot be represented.
    let bodyDeadlineExpiry: BodyDeadlineExpiry | null = null
    // AIDEV-NOTE: headers arriving on a *successful* response is the point after which the
    // backend has, by definition, finished the computation - a failure past it is a delivery
    // fault, and retrying pays for the same computation again without addressing it. This is
    // specifically about failures that interrupt delivery of a result; it is not a blanket
    // "never retry once headers arrive" rule, and in particular the retry-status branch below
    // still retries 5xx responses. Those also arrive with headers, but a 5xx means the
    // computation *failed*, so there is no completed work being thrown away by retrying.
    // bodyDeadlineExpiry only covers the
    // subset of those failures that are our own deadline firing; a stream that breaks on its
    // own (proxy dropping the connection mid-body) surfaces as a plain TypeError with no
    // deadline involved, which is why the retry predicates below key on this record instead.
    let headersReceived = false

    // AIDEV-NOTE (#877): one child span per attempt, created explicitly rather than left to the
    // SDK's fetch instrumentation. That instrumentation only fires when `getActiveSpan()`
    // returns something (@sentry/core fetch.js: `const hasParent = !!getActiveSpan()`), and the
    // outer span is deliberately inactive - see the note in `fetchInitialScrs` for why making
    // it active loses the whole measurement under overlapping submissions. Passing the parent
    // explicitly is better than the ambient version anyway: parentage is decided by this line
    // rather than by whatever happens to be on a global scope stack when the fetch starts, so
    // two concurrent submissions cannot cross-attach each other's attempts. These are what make
    // "did the deadline fire once or four times, and how long did each attempt last" answerable
    // (#843); RESEND_COUNT_ATTRIBUTE alone answers only the first half.
    const attemptSpan = Sentry.startInactiveSpan({
      name: ATTEMPT_SPAN_NAME,
      op: ATTEMPT_SPAN_OP,
      parentSpan: span,
      attributes: {
        "http.request.method": INITIAL_SCRS_REQUEST.method,
        "url.path": INITIAL_SCRS_REQUEST.endpoint,
        [RESEND_COUNT_ATTRIBUTE]: attempt,
      },
    })

    try {
      const response = await fetch(INITIAL_SCRS_REQUEST.endpoint, {
        method: INITIAL_SCRS_REQUEST.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parameters),
        signal: controller.signal,
      })

      // Clear the timeout since we got a response
      clearTimeout(timeoutId)
      headersReceived = true
      attemptSpan.setAttribute("http.response.status_code", response.status)

      if (response.ok) {
        // AIDEV-NOTE: Response headers arriving is not the same as the response being
        // complete. Clearing the deadline above and then reading the body unguarded meant
        // a 200 whose body stalls mid-stream (proxy dropping a long-poll, container cycled
        // mid-response) left this promise pending forever - and callers that flip a
        // "loading" flag before awaiting never get it back (#51). The body therefore gets
        // deadlines of its own, separate from the headers budget: an idle deadline for a
        // stall and a total cap for a trickle (#1554) - see readOkResponseBody.
        const rawData = await readOkResponseBody(response, config, expiry => {
          bodyDeadlineExpiry = expiry
          controller.abort()
        })

        // Validate the response format
        const validationError = validateInitialScrsResponse(rawData)
        if (validationError) {
          throw new Error(
            `Invalid response format for initial SCRs: ${describeInitialScrsValidationError(validationError)}`
          )
        }

        return rawData as InitialScrsTable
      }

      // Consume the response body so its connection is released either way (#477), and fold
      // any server-supplied detail into the thrown error message below.
      // AIDEV-NOTE: this read needs its own deadline for exactly the reason the ok path above
      // does. The header deadline was retired at the top of the try block, so an error body
      // that stalls without the socket closing (nginx returning 502 headers while the upstream
      // hangs) would leave response.text() pending forever, the retry loop never advancing and
      // the caller's "loading" flag stranded - the #51 failure, reached through the error path.
      // consumeResponseBody swallows the resulting AbortError into null, so a stalled error
      // body degrades to the status line rather than propagating as an abort.
      // AIDEV-NOTE: this budget is deliberately far shorter than config.timeout, which sizes
      // the backend's *computation*. Charging the full compute budget per attempt for a body
      // we only ever read a few hundred characters of turned an upstream that stalls its
      // error bodies into ~47s of spinner at the default config (10s x 4 attempts plus
      // backoff) instead of ~7s - the #51 symptom by another route. Clamped so a short test
      // or caller config still bounds it.
      bodyTimeoutId = setTimeout(
        () => {
          controller.abort()
        },
        Math.min(ERROR_BODY_READ_TIMEOUT_MS, config.timeout)
      )
      const bodyText = await consumeResponseBody(response)
      clearTimeout(bodyTimeoutId)
      bodyTimeoutId = null

      // Check if we should retry based on status code
      if (config.retryStatusCodes.includes(response.status) && attempt < config.maxRetries) {
        // AIDEV-NOTE: closed before the backoff wait, not by the `finally` below, so an
        // attempt's duration is the attempt rather than the attempt plus the sleep that
        // follows it. `end()` is idempotent (@sentry/core tracing/sentrySpan.js: "If already
        // ended, skip"), so the `finally` stays a correct catch-all for the paths that do not
        // retry. The same applies at the two retry sites in the catch block below.
        attemptSpan.end()
        attempt++
        await awaitBackoff(attempt, config.retryDelay, externalSignal)
        continue
      }

      // If we're not retrying, throw an error with the server's detail where available
      throw new Error(`Failed to fetch initial SCRs: ${describeNonOkResponse(bodyText, response)}`)
    } catch (error) {
      // Clear the timeouts to prevent memory leaks
      clearTimeout(timeoutId)
      if (bodyTimeoutId !== null) {
        clearTimeout(bodyTimeoutId)
      }

      // AIDEV-NOTE (#479): an aborted external signal means the caller no longer wants a
      // result, so every case below exits here rather than falling into the retry branches,
      // which would otherwise treat the failure exactly like one of our own request/body
      // deadlines and schedule another attempt nobody wants.
      //
      // "Don't retry" is not the same as "call it a cancellation", which is why this is three
      // cases and not two. A failure that merely *coincides* with the abort - the ok-path
      // response.json() resolving after the user cancelled, then failing validation on a
      // genuine backend defect - is a real fault, and reporting it as a cancellation hides it
      // from a caller that filters cancellations out before Sentry. Only an abort-caused
      // failure is typed; anything else surfaces as itself, still without retrying.
      //
      // A backoff wait raised inside the `try` above has already reported its own cancellation,
      // so rethrowing it unchanged is what keeps `cause` the abort reason the caller supplied
      // instead of nesting one InitialScrsCancelledError inside another.
      if (error instanceof InitialScrsCancelledError) {
        throw error
      }
      if (externalSignal?.aborted) {
        // AIDEV-NOTE: typed rather than rethrown raw so a caller can tell "I cancelled this"
        // from "this failed", which the raw DOMException's browser-authored message cannot
        // express. `cause` is the caller's abort reason at every site that constructs this
        // error - the entry guard above, awaitBackoff, and here - so a cancellation carries the
        // value the caller passed to abort() regardless of which rejection happened to notice
        // the abort first. The abort DOMException that lands here says nothing the type does
        // not already say, whereas the reason is the caller's own.
        if (error instanceof DOMException && error.name === "AbortError") {
          throw new InitialScrsCancelledError(externalSignal.reason)
        }
        throw error
      }

      // Check if this was a timeout or abort error
      if (error instanceof DOMException && error.name === "AbortError") {
        // AIDEV-NOTE (#478): the header-phase deadline (armed above) and the two body-phase
        // deadlines (armed once headers arrive) all abort the same controller and surface
        // as an indistinguishable AbortError here. bodyDeadlineExpiry is what tells them
        // apart: a body-phase abort means the backend had already finished the
        // computation, so retrying would just pay for it again without fixing the
        // delivery fault - whichever of the two body deadlines fired (#1554). Fail fast with
        // a distinct, machine-checkable error instead.
        // AIDEV-NOTE: bodyDeadlineExpiry is assigned inside the deadline callback passed to
        // readOkResponseBody; the linter's narrowing doesn't track that assignment across the
        // try/catch and async boundary and reports this as always-false. Verified
        // false-positive in isolation, and the dedicated #478 tests (which fail without this
        // branch) prove it evaluates true at runtime.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (bodyDeadlineExpiry !== null) {
          throw createBodyPhaseTimeoutError(bodyDeadlineExpiry)
        }

        if (attempt < config.maxRetries) {
          attemptSpan.end()
          attempt++
          await awaitBackoff(attempt, config.retryDelay, externalSignal)
          continue
        }
        throw createHeaderPhaseTimeoutError()
      }

      // AIDEV-NOTE (#476): classify structurally rather than pattern-matching message text.
      // No real browser's network-failure message contains the lowercase substring
      // "network" the old check looked for - Chrome throws "Failed to fetch", Safari
      // "Load failed", Firefox "NetworkError..." with a capital N - so this branch was
      // dead code in every major browser. `fetch()` itself only ever rejects with a
      // TypeError for network-layer failures; the one other TypeError reachable in this
      // try block would be JSON.stringify(parameters) on circular input, which validated,
      // already-submitted user parameters won't produce, so retrying any TypeError here is
      // an accepted, deliberate choice rather than an oversight.
      // AIDEV-NOTE: `!headersReceived` is what keeps that choice safe. A body stream that
      // breaks mid-transfer also rejects with a TypeError (undici reports `terminated`), but
      // it happens after the backend finished computing, so retrying it re-runs completed work
      // up to maxRetries times against infrastructure that is already failing - the invariant
      // #478 protects for the deadline case, applied to the non-deadline case too. Only a
      // pre-header TypeError is a genuine "the request never landed" failure worth retrying.
      if (error instanceof TypeError && !headersReceived && attempt < config.maxRetries) {
        attemptSpan.end()
        attempt++
        await awaitBackoff(attempt, config.retryDelay, externalSignal)
        continue
      }

      // If we've reached max retries or it's another type of error, rethrow
      throw error
    } finally {
      // Every route out of this iteration passes here - the success `return`, each retry's
      // `continue`, and every rethrow - so an attempt span cannot be left open. Ending it here
      // also guarantees it is closed before the parent is, which matters: a transaction filters
      // its children through `isFullFinishedSpan` (@sentry/core tracing/sentrySpan.js), which
      // requires a `timestamp`, so a child still open when the parent ends is silently dropped.
      attemptSpan.end()
    }
  }

  // This should never happen, but TypeScript requires a return value
  throw new Error("Max retries exceeded while fetching initial SCRs")
}
